// File: components/Shufflemaps/ListingDetail/CategoryPickerMulti.jsx
import React from 'react';
import {
  FaChalkboardTeacher,
  FaUsers,
  FaBuilding,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaWifi,
  FaUserTie,
  FaInfoCircle,
} from 'react-icons/fa';
import styles from './ListingDetail.module.css';

/**
 * This picker:
 * - Accepts either slugs (e.g., "classes", "1-on-1-classes") OR display names (e.g., "Classes")
 *   via the `valueSlugs` prop — legacy name kept for compatibility.
 * - Emits **display names** by default through onChange(nextNames).
 * - Enforces valid combinations, including allowing "Classes" + "1-on-1 Classes" without "Studios".
 * - Supports hiding options via `excludeSlugs` (accepts slugs or canonical names; case-insensitive).
 * - Displays localized labels using i18n.categories[name] when provided.
 */

const NAME_TO_SLUG = {
  'Classes':        'classes',
  'Meetups':        'meetups',
  'Studios':        'studios',
  'Events':         'events',
  'Spots':          'spots',
  'Online':         'online',
  '1-on-1 Classes': '1-on-1-classes',
};

const SLUG_TO_NAME = Object.fromEntries(
  Object.entries(NAME_TO_SLUG).map(([name, slug]) => [slug, name])
);

const toName = (v) => {
  if (!v && v !== 0) return null;
  const s = String(v).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (SLUG_TO_NAME[lower]) return SLUG_TO_NAME[lower];

  // Common aliases
  if (['spot', 'spots'].includes(lower)) return 'Spots';
  if (['meetup', 'meetups'].includes(lower)) return 'Meetups';
  if (['event', 'events'].includes(lower)) return 'Events';
  if (lower === 'online') return 'Online';
  if (['studio', 'studios'].includes(lower)) return 'Studios';
  if (['class', 'classes'].includes(lower)) return 'Classes';
  if (
    lower === '1-on-1-classes' ||
    lower === '1-on-1' ||
    lower === 'one-on-one' ||
    lower === 'one on one' ||
    lower === '1 on 1'
  ) return '1-on-1 Classes';

  // Already a canonical name?
  if (NAME_TO_SLUG[s]) return s;

  // Unknown — ignore
  return null;
};

// DB category names (authoritative for UI)
export const CATEGORY_OPTIONS = [
  { id: 1, name: 'Classes',          slug: 'classes',          Icon: FaChalkboardTeacher, exclusive: false },
  { id: 2, name: 'Meetups',          slug: 'meetups',          Icon: FaUsers,             exclusive: true  },
  { id: 3, name: 'Studios',          slug: 'studios',          Icon: FaBuilding,          exclusive: false },
  { id: 4, name: 'Events',           slug: 'events',           Icon: FaCalendarAlt,       exclusive: true  },
  { id: 5, name: 'Spots',            slug: 'spots',            Icon: FaMapMarkerAlt,      exclusive: true  },
  { id: 6, name: 'Online',           slug: 'online',           Icon: FaWifi,              exclusive: true  },
  { id: 7, name: '1-on-1 Classes',   slug: '1-on-1-classes',   Icon: FaUserTie,           exclusive: false },
];

export const CATEGORY_BY_NAME = Object.fromEntries(CATEGORY_OPTIONS.map(o => [o.name, o]));
export const CATEGORY_BY_ID   = Object.fromEntries(CATEGORY_OPTIONS.map(o => [o.id, o]));
export const CATEGORY_BY_SLUG = Object.fromEntries(CATEGORY_OPTIONS.map(o => [o.slug, o]));

// ─────────────────────────────────────────────────────────────
// Validation: exclusives alone; allow Classes + 1-on-1 Classes;
// allow Studios + (Classes / 1-on-1 / both); singletons ok.
// ─────────────────────────────────────────────────────────────
function isValidSet(names) {
  const set = new Set(names);

  // Exclusives must be alone
  for (const o of CATEGORY_OPTIONS) {
    if (o.exclusive && set.has(o.name)) {
      return set.size === 1;
    }
  }

  const hasStudios = set.has('Studios');
  const hasClasses = set.has('Classes');
  const hasOneOnOne = set.has('1-on-1 Classes');

  // Allow Classes + 1-on-1 Classes without Studios
  if (!hasStudios) {
    if (set.size === 1 && (hasClasses || hasOneOnOne)) return true;
    if (set.size === 2 && hasClasses && hasOneOnOne) return true;
    return false;
  }

  // Studios present: allow Studios with either or both class types
  if (hasStudios) {
    const allowed = new Set(['Studios', 'Classes', '1-on-1 Classes']);
    if ([...set].some(n => !allowed.has(n))) return false;
    return true;
  }

  return false;
}

function resolveToggle(currentNames, name) {
  const set = new Set(currentNames);

  // If already selected, remove it
  if (set.has(name)) {
    set.delete(name);
    const next = [...set];
    return isValidSet(next) ? next : [];
  }

  // Try adding it
  const tryAdd = [...set, name];
  if (isValidSet(tryAdd)) return tryAdd;

  const meta = CATEGORY_BY_NAME[name];

  // Clicked an exclusive → becomes the only selection
  if (meta?.exclusive) return [name];

  // Smart fallbacks for class-type pairing
  if (name === 'Classes') {
    if (set.has('1-on-1 Classes')) return ['Classes', '1-on-1 Classes'];
    if (set.has('Studios')) return ['Studios', 'Classes'];
    return ['Classes']; // singleton allowed
  }

  if (name === '1-on-1 Classes') {
    if (set.has('Classes')) return ['Classes', '1-on-1 Classes'];
    if (set.has('Studios')) return ['Studios', '1-on-1 Classes'];
    return ['1-on-1 Classes']; // singleton allowed
  }

  if (name === 'Studios') {
    // Keep any class-type that may already be selected
    const keep = [];
    if (set.has('Classes')) keep.push('Classes');
    if (set.has('1-on-1 Classes')) keep.push('1-on-1 Classes');
    const withStudios = ['Studios', ...keep];
    return isValidSet(withStudios) ? withStudios : ['Studios'];
  }

  // Default non-exclusive singleton
  return [name];
}

export default function CategoryPickerMulti({
  valueSlugs = [],                  // accepts slugs *or* canonical names (legacy prop name)
  onChange,
  excludeSlugs = [],                // accepts slugs or canonical names, case-insensitive
  i18n = {},                        // { categories: { 'Classes': '…', ... } }
}) {
  const labelFor = (name) => i18n?.categories?.[name] || name;

  // Normalize current selection to canonical display names
  const selectedNames = (Array.isArray(valueSlugs) ? valueSlugs : [])
    .map(toName)
    .filter(Boolean);
  const selected = new Set(selectedNames);

  // Normalize excludes to slugs for robust matching
  const excludeSlugSet = new Set(
    (Array.isArray(excludeSlugs) ? excludeSlugs : [])
      .map(v => String(v || '').trim().toLowerCase())
      .map(s => SLUG_TO_NAME[s] ? s : (NAME_TO_SLUG[s] ? NAME_TO_SLUG[s] : s)) // name->slug if name provided
  );

  const isAnyExclusive = CATEGORY_OPTIONS.some(o => o.exclusive && selected.has(o.name));

  const disabled = (name) => {
    if (isAnyExclusive) return !selected.has(name);
    return false;
  };

  const handleClick = (name) => {
    const current = Array.from(selected);
    const nextNames = resolveToggle(current, name);
    // Emit display names (HeaderSection can normalize further)
    onChange?.(nextNames);
  };

  return (
    <div className={styles.categoryPicker}>
      {CATEGORY_OPTIONS.filter(opt => !excludeSlugSet.has(opt.slug)).map(({ name, slug, Icon }) => {
        const checked = selected.has(name);
        const label = labelFor(name);
        return (
          <label
            key={slug}
            className={`${styles.categoryOption} ${checked ? styles.checked : ''} ${disabled(name) ? styles.disabled : ''}`}
            onClick={(e) => { e.preventDefault(); handleClick(name); }}
            title={label}
          >
            <input type="checkbox" readOnly checked={checked} aria-label={label} />
            <Icon className={styles.categoryIcon} aria-hidden="true" />
            <span>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

export function CategoryBadge({ slug, i18n = {} }) {
  // Accept slug or name; normalize to canonical display name, then localize for UI
  const canonical = toName(slug) || String(slug || '');
  const Icon = (CATEGORY_BY_NAME[canonical]?.Icon) || FaInfoCircle;
  const label = i18n?.categories?.[canonical] || canonical;
  return (
    <span className={styles.categoryBadge} title={label} aria-label={label}>
      <Icon aria-hidden="true" />
      {label}
    </span>
  );
}
